import { Token } from 'config/types'
import { Game, GameDemoSession } from 'config/types/game'
import { useImmutableValueReference } from 'hooks/useImmutableValueReference'
import { useRequest } from 'hooks/useRequest'
import { useSelectedBalance } from 'layout/Components/BalanceSelect/hooks'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import GameService from 'services/GameService'
import { useAppDispatch, useAppSelector } from 'state'
import { setIsInGame } from 'state/app/actions'
import { useClientIpInfo, useProviderInfo, useValidatePlayToken } from 'state/app/hooks'
import { useAuth } from 'state/auth/hooks'
import { delayed } from 'utils'
import { SentryHelper } from 'utils/sentryHelper'
import useValidateGame from 'views/GameDetails/hooks/useValidateGame'
import { HunnyToast } from 'utils/toastify'

const useGameIframeInfo = (game: Game, gameDemoSession: GameDemoSession) => {
  const [playMode, setPlayMode] = useState<'demo' | 'real'>('real')
  const [realPlayIframeSource, setRealPlayIframeSource] = useState<{ token?: Token; url: string }>(null)
  const [isLoading, setIsLoading] = useState(false)

  const { t } = useTranslation()
  const { execute } = useRequest()
  const dispatch = useAppDispatch()
  const { isSigned, hasSession } = useAuth()
  const isInGame = useAppSelector((state) => state.app.isInGame)

  const { selectedBalance } = useSelectedBalance()
  const playToken = selectedBalance.token
  const { isBlackList } = useValidatePlayToken(playToken)
  const { country } = useClientIpInfo()
  const provider = useProviderInfo(game.providerCode)
  const isBlockedCountry = provider?.blacklistCountry?.includes(country)

  const isInGameRef = useImmutableValueReference(isInGame)

  const validateResult = useValidateGame(game)
  const isDisabledRef = useImmutableValueReference(!validateResult)
  const isRealPlayIframeLoadedRef = useImmutableValueReference(playToken === realPlayIframeSource?.token)
  const isDemoModeRef = useImmutableValueReference(playMode === 'demo')

  const createGameSession = async () => {
    setIsLoading(true)
    await delayed(200)
    if (isDisabledRef.current) return

    const result = await execute(GameService.createGameSession(game.code, playToken.code, playToken.network))
    const { gameUrl } = result?.data || {}

    if (result.code === 'error_game_restricted_by_voucher') {
      HunnyToast.error(t('This game is restricted while your Claim to Play Bonus is active.'))
    }

    if (gameUrl) {
      setRealPlayIframeSource({
        token: playToken,
        url: gameUrl,
      })
      setIsLoading(false)
      return gameUrl
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (!isInGameRef.current) return

    if (playMode == 'demo' && !isBlockedCountry) return
    const isLowBalance = selectedBalance.amount.eq(0)
    if (!hasSession || isBlackList || isBlockedCountry || isLowBalance || game.isMaintenance) {
      dispatch(setIsInGame(false))
    }
  }, [hasSession, playToken, isBlackList, playMode, selectedBalance.token, isBlockedCountry, game.isMaintenance])

  useEffect(() => {
    if (!isInGameRef.current || isRealPlayIframeLoadedRef.current || isDemoModeRef.current) return
    SentryHelper.addBreadcrumbs({
      category: 'gameIframe.createSession',
      message: 'Update game session when change currency',
    })
    createGameSession()
  }, [playToken])

  useEffect(() => {
    if (!isInGame) return
    if (isInGame && isRealPlayIframeLoadedRef.current) return

    SentryHelper.addBreadcrumbs({
      category: 'gameIframe.createSession',
      message: 'Update game session when isInGame changed',
    })
    createGameSession()
  }, [isInGame])

  // Init & remove game session
  useEffect(() => {
    if (isSigned) {
      SentryHelper.addBreadcrumbs({
        category: 'gameIframe.createSession',
        message: 'Initialize game session',
      })
      createGameSession()
    }

    return () => {
      setRealPlayIframeSource(null)
      dispatch(setIsInGame(false))
    }
  }, [isSigned, game.slug])

  const iframeUrl = useMemo(() => {
    return isBlockedCountry
      ? ''
      : playMode == 'demo' || !isSigned
      ? gameDemoSession?.gameDemoURL || ''
      : realPlayIframeSource?.url || ''
  }, [playMode, isSigned, realPlayIframeSource, isBlockedCountry, gameDemoSession])

  return useMemo(
    () => ({
      iframeUrl,
      isLoading,
      setPlayMode,
      playMode,
    }),
    [isLoading, iframeUrl],
  )
}

export default useGameIframeInfo
